import { useState } from "react";

import { format } from "date-fns";

import { DateFormat } from "@/constants/DateFormat";
import { ModalTestID } from "@/constants/TestID";
import SMTEntry from "@/ui/components/smt/ViewSMTEntries/SMTEntry";
import ImageViewerModal from "@/ui/composed/media/ImageViewerModal";
import BottomSheetModal from "@/ui/composed/modal/BottomSheetModal";
import { FAIconName, Icon } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import { mapSMTDailyLogEntryToSMTEntryProps } from "@/utils/smt.util";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = joinTestIDs(ModalTestID.SelfMonitoring, "view-entry");

interface ViewSMTEntryBottomSheetModalProps {
  entry: SMTDailyLogEntry;
  isOpen: boolean;
  onDismiss: () => void;
  onOpen: () => void;
}

const ViewSMTEntryBottomSheetModal = ({
  entry,
  isOpen,
  onDismiss,
  onOpen,
}: ViewSMTEntryBottomSheetModalProps) => {
  const title = entry.occurredAt
    ? `${format(entry.occurredAt, DateFormat.weekDay)} ${format(entry.occurredAt, DateFormat.fullMonthDay)}`
    : "Entry";
  const entryProps = mapSMTDailyLogEntryToSMTEntryProps(entry);
  const [isImageFullScreenOpen, setIsImageFullScreenOpen] =
    useState<boolean>(false);

  const onOpenImageFullScreen = () => {
    if (!entryProps.photoUrl) return;
    setIsImageFullScreenOpen(true);
  };

  // display ImageViewerModal outside of BottomSheetModal
  return (
    <>
      {entryProps.photoUrl && (
        <ImageViewerModal
          contentType={entryProps.photoContentType}
          imageUrl={entryProps.photoUrl}
          isVisible={isImageFullScreenOpen}
          onClose={() => {
            setIsImageFullScreenOpen(false);
            onOpen();
          }}
          testID={joinTestIDs(baseTestID, "image-viewer")}
        />
      )}
      <BottomSheetModal
        dismissAccessibilityLabel="Dismiss Journal Entry"
        icon={
          <Icon
            color={ColorToken.AccentPeriwinkle}
            name={FAIconName.Salad}
            size="lg"
            testID={joinTestIDs(baseTestID, "title")}
          />
        }
        isOpen={isOpen && !isImageFullScreenOpen}
        onRequestClose={onDismiss}
        shouldResizeOnContentChange
        testID={baseTestID}
        title={title}
      >
        <VStack paddingTop={12}>
          <SMTEntry
            avatarUserId={entryProps.avatarUserId}
            avatarUserName={entryProps.avatarUserName}
            avatarUserType={entryProps.avatarUserType}
            details={entryProps.details}
            onOverrideOpenImageFullScreen={onOpenImageFullScreen}
            photoContentType={entryProps.photoContentType}
            photoUrl={entryProps.photoUrl}
            smtEntryId={entryProps.smtEntryId}
            thumbnailUrl={entryProps.thumbnailUrl}
            time={entryProps.time}
          />
        </VStack>
      </BottomSheetModal>
    </>
  );
};

export default ViewSMTEntryBottomSheetModal;
